import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlaceIcon from '@mui/icons-material/Place';
import RateReviewIcon from '@mui/icons-material/RateReview';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TodayIcon from '@mui/icons-material/Today';
import GavelIcon from '@mui/icons-material/Gavel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import { StatCard } from '@/features/admin/components/StatCard';
import { useAdminBookingStats } from '@/features/admin-bookings/hooks/useAdminBookings';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { BookingStatus } from '@/types';
import { GuideStatusChip } from '@/features/admin/components/GuideStatusChip';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import {
  ADMIN_ACTIONABLE_STAGES,
  ONBOARDING_TABS,
} from '@/features/admin/constants/onboardingStages';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardHeroSx, dashboardPanelSx, dashboardSectionPanelSx } from '@/shared/styles/surfaces';

export function AdminDashboardPage() {
  const navigate = useNavigate();

  const { data: stats, isLoading, isError } = useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: adminApi.getDashboard,
  });

  // Counts only — the rows themselves live on the onboarding page, which used
  // to be duplicated here as a second, action-less copy of the queue table.
  const { data: pipeline } = useQuery({
    queryKey: adminKeys.onboarding({ limit: 1 }),
    queryFn: () => adminApi.getOnboardingPipeline({ limit: 1 }),
  });

  const { data: bookingStats } = useAdminBookingStats();
  const bookingsTab = (tab?: string) =>
    navigate(tab ? `${ADMIN_ROUTES.BOOKINGS}?tab=${tab}` : ADMIN_ROUTES.BOOKINGS);
  const activeTrips = bookingStats
    ? bookingStats.byStatus[BookingStatus.CONFIRMED] +
      bookingStats.byStatus[BookingStatus.UPCOMING] +
      bookingStats.byStatus[BookingStatus.IN_PROGRESS]
    : '—';

  const counts = pipeline?.counts;
  const needsAction = ADMIN_ACTIONABLE_STAGES.reduce(
    (total, stage) => total + (counts?.[stage] ?? 0),
    0
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !stats) {
    return <Alert severity="error">Failed to load dashboard analytics. Please refresh.</Alert>;
  }

  const guideStatusEntries = Object.entries(stats.guidesByStatus ?? {}).sort(
    (a, b) => b[1] - a[1]
  );

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...dashboardHeroSx, p: { xs: 3, md: 3.5 }, mb: 4 }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'flex-end' },
              justifyContent: 'space-between',
              gap: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.65)', letterSpacing: '0.14em', fontWeight: 600 }}
              >
                Admin console
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  letterSpacing: '-0.03em',
                  mt: 0.5,
                  lineHeight: 1.15,
                  color: '#FFFFFF',
                }}
              >
                Platform Overview
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 1, maxWidth: 480 }}>
                Monitor travelers and guide verifications across GetGuide.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(ADMIN_ROUTES.ONBOARDING)}
              sx={{ borderRadius: 99, fontWeight: 700, px: 2.5, flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}
            >
              Guide Onboarding ({needsAction})
            </Button>
          </Box>
        </Box>
      </Paper>

      <DashboardSectionHeader title="Key metrics" subtitle="Live platform health at a glance" />
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Registered Travelers"
            value={stats.totalTravelers}
            subtitle={`${stats.recentTravelerSignups} joined in last 7 days`}
            icon={<PeopleIcon />}
            accent="primary.main"
            onClick={() => navigate(ADMIN_ROUTES.TRAVELERS)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Approved Guides"
            value={stats.totalGuides}
            subtitle={`${stats.totalGuideProfiles} total guide profiles`}
            icon={<VerifiedUserIcon />}
            accent="success.main"
            onClick={() => navigate(ADMIN_ROUTES.GUIDES)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Needs Your Action"
            value={needsAction}
            subtitle="Background checks & profile reviews"
            icon={<PendingActionsIcon />}
            accent="warning.main"
            onClick={() => navigate(ADMIN_ROUTES.ONBOARDING)}
          />
        </Grid>
      </Grid>

      <DashboardSectionHeader
        title="Bookings"
        subtitle="Trips, money and anything that needs a look"
        action={
          <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => bookingsTab()}>
            All bookings
          </Button>
        }
      />
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
          <StatCard
            label="Booked Today"
            value={bookingStats?.todayCreated ?? '—'}
            icon={<TodayIcon />}
            accent="primary.main"
            onClick={() => bookingsTab()}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
          <StatCard
            label="Active Trips"
            value={activeTrips}
            icon={<EventNoteIcon />}
            accent="success.main"
            onClick={() => bookingsTab('active')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
          <StatCard
            label="Disputed"
            value={bookingStats?.byStatus[BookingStatus.DISPUTED] ?? '—'}
            icon={<GavelIcon />}
            accent="warning.main"
            onClick={() => bookingsTab('disputed')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
          <StatCard
            label="Needs Attention"
            value={bookingStats?.needsAttention ?? '—'}
            icon={<ReportProblemIcon />}
            accent="warning.main"
            onClick={() => bookingsTab('attention')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, lg: 2.4 }}>
          <StatCard
            label="Booked Value (Month)"
            value={bookingStats ? (formatGuidePrice(bookingStats.gmvThisMonth) ?? '—') : '—'}
            subtitle={bookingStats ? `${bookingStats.monthConfirmedCount} paid bookings` : undefined}
            icon={<CurrencyRupeeIcon />}
            accent="info.main"
          />
        </Grid>
      </Grid>

      <DashboardSectionHeader title="Platform activity" />
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Destinations" value={stats.totalDestinations} icon={<PlaceIcon />} accent="info.main" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Reviews" value={stats.totalReviews} icon={<RateReviewIcon />} accent="warning.main" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Total Users" value={stats.totalUsers} subtitle="Including admins" icon={<PeopleIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="New Travelers (7d)"
            value={stats.recentTravelerSignups}
            icon={<TrendingUpIcon />}
            accent="success.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper elevation={0} sx={{ ...dashboardSectionPanelSx, height: '100%' }}>
            <DashboardSectionHeader title="Guide profiles by status" />
            {guideStatusEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No guide profiles yet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {guideStatusEntries.map(([status, count]) => (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <GuideStatusChip status={status} />
                    <Typography variant="body2" fontWeight={700}>{count}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper elevation={0} sx={{ ...dashboardSectionPanelSx, height: '100%' }}>
            <DashboardSectionHeader
              title="Guide onboarding"
              action={
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(ADMIN_ROUTES.ONBOARDING)}
                >
                  Open pipeline
                </Button>
              }
            />

            <Grid container spacing={2}>
              {ONBOARDING_TABS.map((tab) => (
                <Grid size={{ xs: 12, sm: 6 }} key={tab.stage}>
                  <StatCard
                    label={tab.label}
                    value={counts?.[tab.stage] ?? 0}
                    accent={
                      ADMIN_ACTIONABLE_STAGES.includes(tab.stage)
                        ? 'warning.main'
                        : 'info.main'
                    }
                    onClick={() => navigate(ADMIN_ROUTES.onboarding(tab.stage))}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
